(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  class RG10000Showcase extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.cards = [...this.querySelectorAll('[data-rg-card]')];
      this.controls = [...this.querySelectorAll('.rg-showcase__toolbar [data-rg-select]')];
      this.index = 0;
      this.mobile = window.matchMedia('(max-width: 749px)');
      if (this.dataset.mode === 'scene' && !this.mobile.matches) this.querySelectorAll('.rg-showcase__visual img').forEach(image => { image.loading = 'eager'; });
      this.addEventListener('click', event => {
        const select = event.target.closest('[data-rg-select]');
        const move = event.target.closest('[data-rg-step]');
        const pan = event.target.closest('[data-rg-tab-pan]');
        if (pan) { const strip = this.querySelector('[role="tablist"]'); strip?.scrollBy({ left: Number(pan.dataset.rgTabPan) * strip.clientWidth * .75, behavior: reducedMotion() ? 'instant' : 'smooth' }); }
        if (select || move) clearTimeout(this.autoTimer);
        if (select) this.select(Number(select.dataset.rgSelect));
        if (move) this.select(this.index + Number(move.dataset.rgStep));
      }, { signal });
      this.addEventListener('keydown', event => {
        const control = event.target.closest('[role="tab"]');
        if (!control || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.select((next + this.cards.length) % this.cards.length);
        this.controls[this.index]?.focus();
      }, { signal });
      if (this.dataset.mode === 'accordion') {
        this.cards.forEach((card, index) => card.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && !this.mobile.matches) this.select(index);
        }, { signal }));
      }
      const rail = this.querySelector('[data-rg-rail]');
      if (rail && (this.dataset.mode === 'carousel' || this.dataset.mode === 'tabs')) {
        rail.addEventListener('scroll', () => {
          const centered = this.dataset.mode === 'tabs';
          const origin = rail.getBoundingClientRect().left + (centered ? rail.clientWidth / 2 : 0);
          const nearest = this.cards.reduce((best, card, index) => {
            const distance = Math.abs(card.getBoundingClientRect().left + (centered ? card.clientWidth / 2 : 0) - origin);
            return distance < best.distance ? { index, distance } : best;
          }, { index: 0, distance: Infinity });
          this.index = nearest.index;
          this.sync();
        }, { signal, passive: true });
      }
      document.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.contains(event.target) || card === event.target);
        if (index >= 0) this.select(index);
      }, { signal });
      this.mobile.addEventListener('change', () => { this.select(this.index); this.updateSequence?.(); }, { signal });
      const strip = this.querySelector('[role="tablist"]');
      strip?.addEventListener('scroll', () => this.syncTabEdges(), { signal, passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        this.syncTabEdges(); this.alignMask();
        if (this.dataset.mode === 'tabs' && this.railWidth !== this.clientWidth) { this.railWidth = this.clientWidth; this.scrollToCard(false); }
      });
      this.resizeObserver.observe(this);
      if (this.dataset.mode === 'accordion') this.addEventListener('transitionend', () => this.alignMask(), { signal });
      this.setupCardVideos(signal);
      this.setupSequence(signal);
      this.sync();
    }
    maxIndex() {
      if (this.dataset.mode !== 'carousel') return this.cards.length - 1;
      const rail = this.querySelector('[data-rg-rail]');
      if (!rail || !this.cards.length) return 0;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const visible = Math.max(1, Math.round((this.clientWidth + gap) / (this.cards[0].getBoundingClientRect().width + gap)));
      return Math.max(0, this.cards.length - visible);
    }
    select(index) {
      this.index = Math.max(0, Math.min(this.maxIndex(), index));
      this.sync();
      if (this.dataset.mode === 'carousel' || this.dataset.mode === 'tabs') {
        this.scrollToCard(true);
      }
      this.sync();
    }
    scrollToCard(animate) {
      const rail = this.querySelector('[data-rg-rail]');
      const card = this.cards[this.index];
      if (!rail || !card) return;
      const offset = this.dataset.mode === 'tabs' ? (rail.clientWidth - card.clientWidth) / 2 : 0;
      rail.scrollTo({ left: card.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft - offset, behavior: animate && !reducedMotion() ? 'smooth' : 'instant' });
    }
    sync() {
      const mode = this.dataset.mode;
      this.syncCardVideos();
      this.dataset.activeIndex = String(this.index);
      const label = this.querySelector('[data-rg-label]');
      if (label) label.textContent = this.cards[this.index]?.dataset.title || '';
      this.cards.forEach((card, index) => {
        const active = index === this.index;
        card.classList.toggle('is-active', active);
        card.classList.toggle('is-before', index < this.index);
        if (mode === 'tabs') card.hidden = false;
        if (mode === 'scene') card.hidden = !active;
        card.querySelectorAll('video').forEach(video => { if (!active && ['tabs','scene','accordion'].includes(mode)) video.pause(); });
        const trigger = card.querySelector('[aria-expanded]');
        if (trigger) trigger.setAttribute('aria-expanded', String(active));
      });
      this.querySelectorAll('[data-rg-tab-copy]').forEach(copy => { copy.hidden = Number(copy.dataset.rgTabCopy) !== this.index; });
      this.controls.forEach((control, index) => {
        const active = Number(control.dataset.rgSelect) === this.index;
        control.classList.toggle('is-active', active);
        if (control.getAttribute('role') === 'tab') {
          control.setAttribute('aria-selected', String(active));
          control.tabIndex = active ? 0 : -1;
        } else control.setAttribute('aria-pressed', String(active));
        if (mode === 'carousel') control.hidden = Number(control.dataset.rgSelect) > this.maxIndex();
      });
      if (mode === 'tabs') {
        const strip = this.querySelector('[role="tablist"]');
        const selected = this.controls[this.index];
        if (strip && selected) {
          const left = selected.offsetLeft - this.controls[0].offsetLeft;
          const edge = 40;
          if (left < strip.scrollLeft + edge || left + selected.offsetWidth > strip.scrollLeft + strip.clientWidth - edge) strip.scrollTo({ left: Math.max(0, left - edge), behavior: 'instant' });
        }
        this.syncTabEdges();
      }
      this.querySelectorAll('[data-rg-step]').forEach(button => {
        button.disabled = Number(button.dataset.rgStep) < 0 ? this.index === 0 : this.index >= this.maxIndex();
      });
    }
    setupCardVideos(signal) {
      this.cardVideoObserver?.disconnect();
      this.cardVideos = [...this.querySelectorAll('[data-rg-card-video]')];
      if (!this.cardVideos.length) return;
      this.videoVisible = false;
      this.cardVideos.forEach(video => {
        video.muted = true;
        video.addEventListener('playing', () => {
          if (video.dataset.wanted === 'true') video.classList.add('is-playing');
          else video.pause();
        }, { signal });
        video.addEventListener('error', () => { video.classList.remove('is-playing'); video.dataset.wanted = 'false'; }, { signal });
      });
      this.cardVideoObserver = new IntersectionObserver(entries => {
        this.videoVisible = entries[0].isIntersecting;
        this.syncCardVideos();
      }, { threshold: 0 });
      this.cardVideoObserver.observe(this);
      // Warm the current device's three clips as soon as the component connects.
      this.cardVideos.forEach(video => {
        const src = this.mobile.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
        if (src && !video.getAttribute('src')) { video.preload = 'auto'; video.src = src; video.load(); }
      });
      document.addEventListener('visibilitychange', () => this.syncCardVideos(), { signal });
    }
    syncCardVideos() {
      this.cardVideos?.forEach(video => {
        const selected = video.closest('[data-rg-card]') === this.cards[this.index];
        const src = this.mobile.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
        const wanted = selected && this.videoVisible && !document.hidden && Boolean(src);
        if (video.getAttribute('src') && video.getAttribute('src') !== src) {
          video.pause(); video.removeAttribute('src'); video.load();
          video.classList.remove('is-playing'); video.dataset.wanted = 'false';
        }
        if (!wanted) {
          video.dataset.wanted = 'false'; video.pause(); video.classList.remove('is-playing');
          if (!selected && video.readyState > 0) video.currentTime = 0;
          return;
        }
        if (video.dataset.wanted === 'true') return;
        video.dataset.wanted = 'true';
        if (video.getAttribute('src') !== src) { video.src = src; video.load(); }
        video.play()?.catch(() => {
          if (video.paused) { video.classList.remove('is-playing'); video.dataset.wanted = 'false'; }
        });
      });
    }
    syncTabEdges() {
      const strip = this.querySelector('[role="tablist"]');
      if (!strip || this.dataset.mode !== 'tabs') return;
      this.querySelectorAll('[data-rg-tab-pan]').forEach(button => {
        button.hidden = Number(button.dataset.rgTabPan) < 0 ? strip.scrollLeft <= 1 : strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
      });
    }
    alignMask() {
      if (this.dataset.mode !== 'accordion') return;
      const height = Math.max(0, ...this.cards.filter(card => !card.classList.contains('is-active')).map(card => card.querySelector('h3')?.getBoundingClientRect().height || 0));
      this.style.setProperty('--rg-mask-height', `${Math.max(160, height + 80)}px`);
    }
    setupSequence(signal) {
      if (this.dataset.mode !== 'accordion' || reducedMotion()) return;
      if (this.dataset.scrollSequence === 'true') {
        const section = this.closest('.rg-showcase-section');
        section.style.setProperty('--rg-sequence-count', this.cards.length);
        const update = () => {
          if (!this.isConnected) return;
          section.classList.toggle('rg-scroll-sequence', this.mobile.matches);
          if (!this.mobile.matches) return;
          const index = Math.floor(Math.max(0, 56 - section.getBoundingClientRect().top) / Math.max(1, innerHeight * .7));
          const next = Math.min(this.cards.length - 1, index);
          if (next !== this.index) this.select(next);
        };
        this.updateSequence = update;
        window.addEventListener('scroll', update, { signal, passive: true });
        window.addEventListener('resize', update, { signal });
        update();
      }
      if (this.dataset.autoAdvance === 'true') {
        const schedule = () => {
          clearTimeout(this.autoTimer);
          if ((this.mobile.matches && this.dataset.scrollSequence === 'true') || !this.inView || this.matches(':hover') || this.contains(document.activeElement) || this.index >= this.cards.length - 1) return;
          this.autoTimer = setTimeout(() => { this.select(this.index + 1); schedule(); }, Math.max(1, Math.min(5, Number(this.dataset.interval) || 3)) * 1000);
        };
        this.visibilityObserver = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; schedule(); }, { threshold: .5 });
        this.visibilityObserver.observe(this);
        this.addEventListener('pointerenter', () => clearTimeout(this.autoTimer), { signal });
        this.addEventListener('pointerleave', schedule, { signal });
        this.addEventListener('focusin', () => clearTimeout(this.autoTimer), { signal });
        this.addEventListener('focusout', schedule, { signal });
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.visibilityObserver?.disconnect(); this.cardVideoObserver?.disconnect(); clearTimeout(this.autoTimer); this.querySelectorAll('video').forEach(video => video.pause()); }
  }
  if (!customElements.get('rg10000-showcase')) customElements.define('rg10000-showcase', RG10000Showcase);

  class RG10000Video extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.preview = this.querySelector('.rg-banner__button-video');
      if (this.preview) {
        this.previewObserver = new IntersectionObserver(entries => {
          this.previewVisible = entries[0].isIntersecting;
          if (this.previewVisible && !reducedMotion() && !this.dialog?.open) this.preview.play()?.catch(() => {});
          else this.preview.pause();
        });
        this.previewObserver.observe(this.querySelector('[data-rg-play]') || this);
      }
      this.addEventListener('click', event => {
        if (event.target.closest('[data-rg-play]')) this.open();
        if (event.target.closest('[data-rg-close]') || event.target === this.dialog) this.dialog?.close();
      }, { signal });
      this.dialog?.addEventListener('close', () => {
        this.dialog.querySelector('video')?.pause();
        if (this.previewVisible && !reducedMotion()) this.preview?.play()?.catch(() => {});
        this.querySelector('[data-rg-play]')?.focus();
      }, { signal });
    }
    open() {
      if (!this.dialog) return;
      if (!this.dialog.querySelector('video')) this.dialog.append(this.querySelector('template').content.cloneNode(true));
      this.preview?.pause();
      this.dialog.showModal();
      const video = this.dialog.querySelector('video');
      if (video?.readyState === 0) video.load();
      video?.play()?.catch(() => {});
    }
    disconnectedCallback() { this.dialog?.querySelector('video')?.pause(); this.preview?.pause(); this.previewObserver?.disconnect(); this.controller?.abort(); }
  }
  if (!customElements.get('rg10000-video')) customElements.define('rg10000-video', RG10000Video);

  function enhanceProduct(root) {
    if (root.dataset.rgProductReady) return;
    root.dataset.rgProductReady = 'true';
    const initialURL = new URL(location.href);
    const previewView = initialURL.searchParams.get('view');
    customElements.whenDefined('product-info').then(() => {
      if (!root.isConnected || previewView !== 'rg10000') return;
      const build = root.buildRequestUrlWithParams.bind(root);
      root.buildRequestUrlWithParams = (...args) => `${build(...args)}&view=rg10000`;
      const update = root.updateURL.bind(root);
      root.updateURL = (...args) => {
        update(...args);
        const url = new URL(location.href);
        url.searchParams.set('view', 'rg10000');
        if (initialURL.pathname.endsWith('/products_preview')) {
          url.pathname = initialURL.pathname;
          url.searchParams.set('preview_key', initialURL.searchParams.get('preview_key'));
        }
        history.replaceState({}, '', url);
      };
    });
    const fallback = root.querySelector('[data-rg-product-fallback]');
    if (fallback && typeof subscribe === 'function') {
      const images = JSON.parse(fallback.querySelector('[data-rg-color-images]').textContent);
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== root.dataset.section || !data.variant) return;
        const name = data.variant.options.join(' ').toLowerCase();
        const color = Object.keys(images).find(key => name.includes(key)) || 'green';
        const image = fallback.querySelector('img');
        image.src = images[color];
        image.alt = `${root.querySelector('h1').textContent} — ${data.variant.title}`;
      });
      const cleanup = event => {
        if (!event.target.contains(root)) return;
        unsubscribe();
        document.removeEventListener('shopify:section:unload', cleanup);
      };
      document.addEventListener('shopify:section:unload', cleanup);
    }
  }

  const init = () => document.querySelectorAll('product-info').forEach(root => {
    if (root.querySelector('script[src*="rg10000-product.js"]')) enhanceProduct(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
